import { Select, MenuItem, Divider, Box, Typography } from '@mui/material';
import { useTimezone } from "../contexts/TimezoneContext";

const formatUtcOffsetLabel = (offsetMinutes: number): string => {
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    const paddedHours = String(hours).padStart(2, "0");

    if (minutes === 0) {
        return `UTC${sign}${paddedHours}`;
    }

    const paddedMinutes = String(minutes).padStart(2, "0");
    return `UTC${sign}${paddedHours}:${paddedMinutes}`;
};

export default function TimezoneSelector() {
    const { selectedTimezone, setSelectedTimezone, timezoneOptions, userTimezone, projectTimezone } = useTimezone();
    const lastPinnedIndex = (projectTimezone && projectTimezone !== userTimezone) ? 1 : 0;

    return (
        <Select
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
                height: 32,
                fontSize: '0.8rem',
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '& .MuiSelect-select': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '32px !important',
                },
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' }
            }}
            renderValue={(value) => {
                // Find the option object that matches the current value
                const selectedOption = timezoneOptions.find(opt => opt.value === value);

                return (
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', ml: 1 }}>
                        {selectedOption ? formatUtcOffsetLabel(selectedOption.offset) : ''}
                    </Typography>
                );
            }}
            MenuProps={{
                sx: { zIndex: 12000 },
                PaperProps: {
                    sx: {
                        bgcolor: '#1a2027',
                        color: 'white',
                        maxHeight: 400,
                        width: 320, // Wider to accommodate the offset on the right
                        '& .MuiMenuItem-root': {
                            fontSize: '0.8rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1
                        }
                    }
                }
            }}
        >
            {timezoneOptions.map((option, index) => {
                const isUserTz = option.value === userTimezone;
                // A project TZ is only "pinned" if it's not a duplicate of the user TZ
                const isProjectTz = option.value === projectTimezone && projectTimezone !== userTimezone;
                const isPinned = isUserTz || isProjectTz;
                const shouldHighlight = isProjectTz && (projectTimezone !== userTimezone);

                // Create the MenuItem
                const menuItem = (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                            // Add a subtle amber left border if this is the timezone they should be using
                            borderLeft: shouldHighlight ? '3px solid #ffa726' : '3px solid transparent',
                            bgcolor: shouldHighlight ? 'rgba(255, 167, 38, 0.05)' : 'transparent',
                            '&:hover': {
                                bgcolor: shouldHighlight ? 'rgba(255, 167, 38, 0.1) !important' : ''
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {isPinned && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        // If mismatched, use the warning color for the label
                                        color: shouldHighlight ? '#ffa726' : (isUserTz ? '#64b5f6' : '#4db6ac'),
                                        fontWeight: 'bold',
                                        fontSize: '0.65rem'
                                    }}
                                >
                                    {isUserTz ? 'Browser Time' : 'Project Center'}
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                {/* Primary Location Name */}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {option.label}
                                </Typography>

                                {/* Secondary Timezone ID (e.g., Europe/Warsaw) */}
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}
                                >
                                    {option.value}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ opacity: 0.5, ml: 2 }}>
                            {formatUtcOffsetLabel(option.offset)}
                        </Typography>
                    </MenuItem>
                );

                if (index === lastPinnedIndex) {
                    return [
                        menuItem,
                        <Divider key="pinned-divider" sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
                    ];
                }

                return menuItem;
            })}
        </Select>
    );
}
